import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchCategories, /* fetchPublicCategories, */ Category } from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface CategoriesContextType {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refetchCategories: () => Promise<void>;
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

interface CategoriesProviderProps {
  children: ReactNode;
}

export const CategoriesProvider: React.FC<CategoriesProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // OPTIMIZATION: Only load categories for authenticated users to reduce database costs
      // Public users don't need categories as they're mainly used for:
      // - Creating activities (authenticated only)
      // - Filtering activities (authenticated only)
      // - Category browsing (disabled on public homepage)
      if (!user) {
        console.log('🏷️ Skipping categories load for public user (cost optimization)');
        setCategories([]);
        setLoading(false);
        return;
      }
      
      // Use authenticated API for logged-in users
      const data = await fetchCategories();
      console.log(`🏷️ Categories loaded for authenticated user:`, data?.length || 0);
      
      // Ensure we always set an array
      if (Array.isArray(data)) {
        setCategories(data);
      } else {
        console.warn('⚠️ Categories data is not an array:', data);
        setCategories([]);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const refetchCategories = async () => {
    await loadCategories();
  };

  useEffect(() => {
    // Only load categories after auth state is stable
    if (!authLoading) {
      // Delay category loading to not block initial render
      const timer = setTimeout(() => {
        loadCategories();
      }, 300); // Load categories after other critical data
      
      return () => clearTimeout(timer);
    }
  }, [user, authLoading]); // Reload categories when user authentication state changes

  const value: CategoriesContextType = {
    categories,
    loading,
    error,
    refetchCategories,
  };

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
}; 